import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/lib/Modal';
import classnames from 'classnames';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import Button from '@/shared/components/Button';

import api from '@/shared/api';
import config from '@/config';
import handleHttpError from '@/shared/services/HandleHttpError';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import messages from './documentTemplateMessages';

const createSearchFilter = search => {
  if (!search || typeof search !== 'string') return () => true;
  const value = search.toLowerCase();
  return p =>
    p.tag.toLowerCase().indexOf(value) >= 0 ||
    (p.description && p.description.toLowerCase().indexOf(value) >= 0);
};

class TaskPlaceholdersModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      taskTemplateId: null,
      taskTemplates: [],
      placeholders: null,
      searchValue: '',
      taskParameters: [],
    };
  }

  componentDidMount() {
    this.retrievePlaceholders();
    this.retrieveTaskTemplates();
  }

  retrievePlaceholders = () => {
    const { intl } = this.props;

    api.base.get(`${config.appUrls.api}/api/documentTemplate/Placeholders/-10`).then(
      ({ data }) => {
        this.setState({
          placeholders: data.map(p => {
            const tag = `{${p}}`;
            const description = intl.formatMessage(listItemMsg('mailplaceholder', tag, ' '));
            return { tag, description };
          }),
        });
      },
      error => {
        handleHttpError(intl)(error);
        return error;
      },
    );
  };
  retrieveTaskParameters = () => {
    const { taskTemplateId } = this.state;
    if (!taskTemplateId) return;
    api.base
      .get(`${config.appUrls.tasks}/api/TaskParameters/Definitions/${taskTemplateId}`)
      .then(({ data }) => {
        this.setState({
          taskParameters: data
            .filter(x => !x.relationFieldType)
            .map(x => ({ tag: `{${x.name}}`, description: x.name }))
            .sort(dynamicSort('description')),
        });
      });
  };

  retrieveTaskTemplates = () => {
    const { intl } = this.props;
    api.base.get(`${config.appUrls.tasks}/api/TaskTemplates`).then(({ data }) => {
      const translatedTemplates = data
        .map(t => ({
          ...t,
          label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)), // Map translation to label property
        }))
        .sort(dynamicSort('label'));
      this.setState({ taskTemplates: translatedTemplates });
    });
  };

  taskTemplateChanged = taskTemplateId => {
    this.setState({ taskTemplateId, taskParameters: [] }, this.retrieveTaskParameters);
  };

  render() {
    const { taskTemplateId, placeholders, searchValue, taskTemplates, taskParameters } = this.state;
    const { onClose, intl } = this.props;

    let filteredPlaceholders = placeholders || [];
    let filteredTaskParameters = taskParameters || [];
    if (searchValue) {
      const searchFilter = createSearchFilter(searchValue);
      filteredPlaceholders = filteredPlaceholders.filter(searchFilter);
      filteredTaskParameters = filteredTaskParameters.filter(searchFilter);
    }

    return (
      <Modal show onHide={() => onClose(null)} keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FormattedMessage {...messages.PlaceholdersModalTitle} />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="scrollable">
          <div className="form-horizontal">
            {taskTemplates && taskTemplates.length > 0 ? (
              <div className="form-group">
                <label className="control-label col-sm-4">
                  <FormattedMessage {...messages.TaskTemplate} />
                </label>
                <div className="col-sm-8">
                  <select
                    className="form-control"
                    value={taskTemplateId || ''}
                    onChange={e => this.taskTemplateChanged(e.target.value || null)}
                  >
                    {taskTemplateId === null && (
                      <option value="">{intl.formatMessage(messages.TaskTemplate)}</option>
                    )}
                    {taskTemplates.map(x => (
                      <option key={x.id} value={x.id}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : null}

            <label>
              <FormattedMessage {...messages.PlaceholdersModalInfo} />
            </label>
            {placeholders && placeholders.length > 0 ? (
              <div className="form-group">
                <div className="col-sm-12">
                  <input
                    type="text"
                    value={searchValue || ''}
                    className="form-control"
                    onChange={e => this.setState({ searchValue: e.target.value || '' })}
                    placeholder={intl.formatMessage(messages.Search)}
                  />
                </div>
              </div>
            ) : null}
            {filteredTaskParameters.map(p => (
              <div key={p.tag} className="row">
                <div className="col-md-6 break-word">{p.tag}</div>
                <div className="col-md-6 break-word">{p.description}</div>
              </div>
            ))}
            {filteredTaskParameters.length > 0 && filteredPlaceholders.length > 0 && <hr />}
            {filteredPlaceholders.map((p, i, a) => (
              <div
                key={p.tag}
                className={classnames('row', {
                  margin10B: a[i + 1] && a[i + 1].tag.charAt(1) !== p.tag.charAt(1),
                })}
              >
                <div className="col-md-6 break-word">{p.tag}</div>
                <div className="col-md-6 break-word">{p.description}</div>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => onClose(null)}>
            <FormattedMessage {...messages.Close} />
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

TaskPlaceholdersModal.propTypes = {
  intl: intlShape.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default injectIntl(TaskPlaceholdersModal);
